/**
 * Smart Menu eligibility and pricing utilities — single source of truth.
 *
 * isEligibleForSmartMenuDiscount — pure eligibility predicate (mirrors backend rule).
 * applySmartPricing             — cart-level discount computation using per-item eligibility.
 */

// Category name substrings that signal an already-promotional category.
const PROMO_KEYWORDS = [
  "oferta", "ofert", "promo", "promocion", "promocao", "promoção",
  "combinado", "combo", "rodizio", "rodízio", "especial", "especiais",
  "feria", "féria", "descuento", "desconto", "happy hour", "deal", "outlet",
];

/**
 * Returns true only when a product is eligible for the global smart-menu discount.
 *
 * A product is NOT eligible when:
 *   - It already has its own discount (discount_percentage > 0)
 *   - It has an old / compare-at price (original_price, old_price, compare_at_price, promo_price)
 *   - It is explicitly flagged as promotional (is_promo, is_offer, featured_offer)
 *   - Its category name matches a promotional keyword
 *
 * @param {object} product      - Product object (from DB or cart item).
 * @param {string} categoryName - Category display name (raw string).
 * @param {object} [campaign]   - Reserved for future campaign-level rules.
 */
export function isEligibleForSmartMenuDiscount(product, categoryName = "", _campaign = null) {
  if (!product) return true; // custom / staff entries are always eligible

  // Rule 1: product already has its own discount percentage
  if ((product.discount_percentage || product.discount_percent || 0) > 0) return false;

  // Rule 2: product has an old / compare-at price (already on promotion)
  for (const field of ["original_price", "old_price", "compare_at_price", "promo_price", "promotional_price"]) {
    const val = product[field];
    if (val && Number(val) > 0) return false;
  }

  // Rule 3: explicitly flagged as promotional
  if (product.is_promo || product.is_offer || product.featured_offer) return false;

  // Rule 4: category name matches a promotional keyword
  const catLow = (categoryName || "").toLowerCase();
  if (PROMO_KEYWORDS.some(kw => catLow.includes(kw))) return false;

  return true;
}

/**
 * Compute cart totals applying the smart-menu global discount only to eligible items.
 *
 * Uses item.price (full unit price including variations) — never originalPrice,
 * which can be 0 for combos (e.g. combo.original_price when no original exists).
 *
 * @param {Array}  cart         - Cart items (each item spreads the product object).
 * @param {object} smartPricing - { active: bool, discount: number (percent) }
 * @param {object} categoryMap  - Map of category_id → category name for eligibility lookup.
 */
export function applySmartPricing(cart, smartPricing, categoryMap = {}) {
  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const pct = smartPricing?.active ? (smartPricing.discount ?? 0) : 0;

  let discountAmount = 0;
  if (pct > 0) {
    const eligibleSubtotal = cart.reduce((s, i) => {
      // Cart items spread product fields; category_name may be stored directly on the item.
      const catName = categoryMap[i.category_id] || i.category_name || "";
      return isEligibleForSmartMenuDiscount(i, catName) ? s + i.price * i.quantity : s;
    }, 0);
    discountAmount = eligibleSubtotal > 0 ? Math.round(eligibleSubtotal * pct) / 100 : 0;
  }

  const total = Math.max(0, subtotal - discountAmount);

  console.debug(
    "[SmartPricing] active=%s percent=%s subtotal=%s eligible_discount=%s total=%s",
    smartPricing?.active ?? false,
    pct,
    subtotal.toFixed(2),
    discountAmount.toFixed(2),
    total.toFixed(2)
  );

  return { subtotal, discountAmount, total };
}
